import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, getImageUrl } from '../../lib/utils';
import { Star, Phone, FileText, FileSpreadsheet, File, FileQuestion, Download, CalendarClock } from 'lucide-react';
import { useContactSettings } from '../../hooks/useContactSettings';
import ConsultationModal from '../modals/ConsultationModal';

const getFileKind = (type = "") => {
  if (type.includes("pdf")) return "pdf";
  if (type.includes("sheet") || type.includes("excel")) return "excel";
  if (type.includes("word") || type.includes("document")) return "word";
  return "file";
};

const KIND_META = {
  pdf: { icon: FileText, bg: "bg-red-50", text: "text-red-600" },
  excel: { icon: FileSpreadsheet, bg: "bg-emerald-50", text: "text-emerald-600" },
  word: { icon: File, bg: "bg-blue-50", text: "text-blue-600" },
  file: { icon: File, bg: "bg-admin-slate-100", text: "text-admin-slate-600" },
};

const ClinicPackageCard = ({ pkg, className }) => {
  if (!pkg || !pkg._id) {
    return null;
  }

  const [isConsultModalOpen, setIsConsultModalOpen] = React.useState(false);

  const navigate = useNavigate();
  const { contactSettings } = useContactSettings();
  const enquiryPhone = contactSettings?.phone?.replace(/[^\d+]/g, "");

  const formatPrice = (price) => (price || 0).toLocaleString();

  const discountPercent = Number(pkg.discountPercent) || 0;
  const hasDiscount = discountPercent > 0;
  const primaryDoc = pkg.documents?.[0];
  const docMeta = primaryDoc ? KIND_META[getFileKind(primaryDoc.type)] : null;
  const DocIcon = docMeta?.icon;

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('[role="button"]')) {
      return;
    }
    navigate(`/clinic-setup/package/${pkg._id}`);
  };

  const handleCallClick = (e) => {
    e.stopPropagation();
    if (!enquiryPhone) {
      e.preventDefault();
    }
  };

  return (
    <div
      className={cn(
        "group relative bg-white rounded-md overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-lg transition-shadow duration-300 h-full flex flex-col border border-gray-100 cursor-pointer transform-gpu",
        className
      )}
      onClick={handleCardClick}
    >
      {/* Media Container */}
      <div className="relative w-full aspect-[4/3] bg-gray-50 overflow-hidden group-hover:bg-gray-100 transition-colors">
        {(pkg.images?.[0] || pkg.image) ? (
          <img
            src={getImageUrl(pkg.images?.[0] || pkg.image)}
            alt={pkg.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&q=80';
            }}
          />
        ) : primaryDoc ? (
          <div className={cn("w-full h-full flex flex-col items-center justify-center gap-2 p-4 text-center", docMeta?.bg)}>
            {DocIcon && <DocIcon className={cn("w-12 h-12 flex-shrink-0 opacity-80", docMeta?.text)} strokeWidth={1.5} />}
            <span className={cn("text-xs font-semibold px-2 break-words max-w-full", docMeta?.text)}>
              {primaryDoc.name}
            </span>
          </div>
        ) : (
          <div className="w-full h-full bg-admin-slate-50 flex items-center justify-center">
            <span className="text-4xl font-bold text-gray-300 opacity-50">{pkg.name?.charAt(0)}</span>
          </div>
        )}

        {/* Badges (Top Left & Right) */}
        <div className="absolute top-2 left-2 right-2 sm:top-3 sm:left-3 sm:right-3 flex justify-between items-start pointer-events-none">
          <div className="flex flex-col gap-1.5">
            {hasDiscount && (
              <div className="bg-red-600 text-white text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded shadow-sm">
                {discountPercent}% OFF
              </div>
            )}
          </div>
          {pkg.isFeatured && (
            <div className="bg-emerald-500 text-white text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded shadow-sm flex items-center gap-0.5 sm:gap-1 ml-auto">
              <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-current" />
              POPULAR
            </div>
          )}
        </div>
      </div>

      {/* Package Information */}
      <div className="flex-grow flex flex-col p-3 sm:p-4">
        {/* Document Attachment Badge (Moved outside image) */}
        {primaryDoc && (pkg.images?.[0] || pkg.image) && (
          <a
            href={primaryDoc.url}
            download
            title={`Download ${primaryDoc.name}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 sm:gap-1.5 bg-gray-50 text-gray-700 text-[9px] sm:text-[10px] font-semibold px-1.5 py-1 sm:px-2 sm:py-1.5 rounded mb-2 hover:bg-gray-100 transition-colors w-fit border border-gray-100 max-w-full"
          >
            <DocIcon className={cn("w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0", docMeta?.text)} />
            <span className="truncate max-w-[80px] sm:max-w-[140px]">{primaryDoc.name}</span>
            {pkg.documents?.length > 1 && (
              <span className="text-gray-400 bg-gray-200 px-1 rounded text-[9px] sm:text-[10px] flex-shrink-0">+{pkg.documents.length - 1}</span>
            )}
          </a>
        )}
        {pkg.type && (
          <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-secondary-600 mb-0.5 sm:mb-1">
            {pkg.type}
          </span>
        )}
        <h3 className="text-sm sm:text-base font-bold text-black mb-1.5 line-clamp-1 leading-tight hover:text-gray-700 transition-colors text-left">
          {pkg.name}
        </h3>

        <p className="text-xs sm:text-sm text-[#6B7280] line-clamp-2 mb-3 leading-snug text-left">
          {pkg.description || pkg.priceNote || "Clinic setup package"}
        </p>

        <div className="w-full h-px bg-gray-100 mb-2 sm:mb-3"></div>

        <div className="mt-auto flex flex-col gap-2">
          {/* Price */}
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
            <span className="text-[15px] sm:text-lg font-bold text-[#111827] leading-none whitespace-nowrap">
              Rs. {formatPrice(pkg.price)}
            </span>
            {pkg.priceNote && (
              <span className="text-[9px] sm:text-xs text-[#6B7280] font-medium leading-tight">
                {pkg.priceNote}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2 w-full">
            <button
              className="flex-1 flex items-center justify-center gap-1 h-7 sm:h-9 rounded sm:rounded-lg border border-[#1B365D] bg-[#1B365D] text-white text-[10px] sm:text-xs font-bold hover:bg-[#152A4A] transition-colors pointer-events-auto shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsConsultModalOpen(true);
              }}
            >
              <CalendarClock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span>Book</span>
            </button>
            <a
              href={enquiryPhone ? `tel:${enquiryPhone}` : "#"}
              className="w-7 h-7 sm:w-9 sm:h-9 rounded sm:rounded-lg flex items-center justify-center transition-colors pointer-events-auto bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 flex-shrink-0"
              onClick={handleCallClick}
              title="Call to Enquire"
            >
              <Phone className="h-4 w-4" strokeWidth={2} />
            </a>
          </div>
        </div>
      </div>

      <ConsultationModal
        isOpen={isConsultModalOpen}
        onClose={() => setIsConsultModalOpen(false)}
        pkg={pkg}
      />
    </div>
  );
};

export default ClinicPackageCard;
